import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { AiChatService as AiChatApiService } from '../api/client/api/aiChat.service';
import { AiChatExecutedActionDto } from '../api/client/model/aiChatExecutedActionDto';
import { AiChatPendingToolCallDto } from '../api/client/model/aiChatPendingToolCallDto';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  executedActions?: AiChatExecutedActionDto[];
  requiresConfirmation?: boolean;
  pendingToolCalls?: AiChatPendingToolCallDto[];
}

interface StoredConversation {
  conversationId: string;
  messages: ChatMessage[];
  lastActivity: string;
}

const STORAGE_KEY = 'ai-chat.conversation';
const CONVERSATION_TTL_MS = 30 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class ChatService {
  private readonly apiService = inject(AiChatApiService);

  readonly messages = signal<ChatMessage[]>([]);
  readonly isLoading = signal(false);
  readonly errorMessage = signal<string | null>(null);
  readonly hasConsented = signal(false);
  readonly hasSharedForImprovementConsented = signal(false);
  readonly isAiChatEnabled = signal(false);

  private conversationId: string | null = null;

  constructor() {
    this.restoreFromStorage();
  }

  async checkConsent(): Promise<void> {
    const consent = await firstValueFrom(
      this.apiService.apiV2AiChatConsentGet()
    );
    this.hasConsented.set(consent.hasConsented);
    this.hasSharedForImprovementConsented.set(
      consent.hasSharedForImprovementConsented
    );
  }

  async loadConfig(): Promise<void> {
    const config = await firstValueFrom(this.apiService.apiV2AiChatConfigGet());
    this.isAiChatEnabled.set(config.aiChatEnabled);
  }

  async grantConsent(shareForImprovement: boolean): Promise<void> {
    await firstValueFrom(
      this.apiService.apiV2AiChatConsentPost({ shareForImprovement })
    );
    this.hasConsented.set(true);
    this.hasSharedForImprovementConsented.set(shareForImprovement);
  }

  async sendMessage(text: string): Promise<void> {
    this.messages.update((msgs) => [...msgs, { role: 'user', content: text }]);
    this.isLoading.set(true);
    this.errorMessage.set(null);

    try {
      const response = await firstValueFrom(
        this.apiService.apiV2AiChatPost({
          message: text,
          conversationId: this.conversationId,
        })
      );
      this.conversationId = response.conversationId;
      this.messages.update((msgs) => [
        ...msgs,
        {
          role: 'assistant',
          content: response.message ?? '',
          executedActions: response.executedActions ?? [],
          requiresConfirmation: response.requiresConfirmation ?? false,
          pendingToolCalls: response.pendingToolCalls ?? [],
        },
      ]);
      this.saveToStorage();
    } catch (error) {
      if (error instanceof HttpErrorResponse && error.status === 429) {
        this.errorMessage.set(
          'Du hast zu viele Anfragen gesendet. Bitte warte einen Moment und versuche es erneut.'
        );
      } else {
        this.errorMessage.set(
          'Es ist ein Fehler aufgetreten. Bitte versuche es erneut.'
        );
      }
    } finally {
      this.isLoading.set(false);
    }
  }

  async confirmBatchOperation(
    pendingToolCalls: AiChatPendingToolCallDto[]
  ): Promise<void> {
    // Mark the confirmation message as resolved
    this.messages.update((msgs) =>
      msgs.map((m) =>
        m.requiresConfirmation ? { ...m, requiresConfirmation: false } : m
      )
    );

    this.messages.update((msgs) => [
      ...msgs,
      { role: 'user', content: 'Bestätigt ✓' },
    ]);
    this.isLoading.set(true);
    this.errorMessage.set(null);

    try {
      const response = await firstValueFrom(
        this.apiService.apiV2AiChatPost({
          message: 'Ja, bitte die Änderungen ausführen.',
          conversationId: this.conversationId,
          confirmedToolCalls: pendingToolCalls,
        })
      );
      this.conversationId = response.conversationId;
      this.messages.update((msgs) => [
        ...msgs,
        {
          role: 'assistant',
          content: response.message ?? '',
          executedActions: response.executedActions ?? [],
          requiresConfirmation: response.requiresConfirmation ?? false,
          pendingToolCalls: response.pendingToolCalls ?? [],
        },
      ]);
      this.saveToStorage();
    } catch (error) {
      this.errorMessage.set(
        'Es ist ein Fehler aufgetreten. Bitte versuche es erneut.'
      );
    } finally {
      this.isLoading.set(false);
    }
  }

  async cancelBatchOperation(): Promise<void> {
    this.messages.update((msgs) =>
      msgs.map((m) =>
        m.requiresConfirmation ? { ...m, requiresConfirmation: false } : m
      )
    );

    await this.sendMessage('Nein, bitte die Änderungen nicht ausführen.');
  }

  async clearConversation(): Promise<void> {
    if (this.conversationId) {
      try {
        await firstValueFrom(
          this.apiService.apiV2AiChatConversationIdDelete(this.conversationId)
        );
      } catch {
        // Silently ignore — conversation already cleaned up on server
      }
    }
    this.conversationId = null;
    this.messages.set([]);
    this.errorMessage.set(null);
    ChatService.clearStorage();
  }

  private static readonly GUID_REGEX =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  private restoreFromStorage(): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;

      const stored: StoredConversation = JSON.parse(raw);
      if (
        !stored.conversationId ||
        !ChatService.GUID_REGEX.test(stored.conversationId)
      ) {
        ChatService.clearStorage();
        return;
      }

      const age = Date.now() - new Date(stored.lastActivity).getTime();
      if (age >= CONVERSATION_TTL_MS) {
        ChatService.clearStorage();
        return;
      }

      this.conversationId = stored.conversationId;
      this.messages.set(stored.messages);
    } catch {
      ChatService.clearStorage();
    }
  }

  private saveToStorage(): void {
    const stored: StoredConversation = {
      conversationId: this.conversationId,
      messages: this.messages(),
      lastActivity: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  }

  static clearStorage(): void {
    localStorage.removeItem(STORAGE_KEY);
  }
}
